import type {
  ChartConfig,
  ChartDataPoint,
  HeatmapChartDatum,
  HeatmapChartProps,
  KPITileProps,
  NumberFormatSpec,
  SeriesColorConfig,
} from '@highereducation/hen-ui';

import type { PivotConfig, TransformRow } from '../transforms/types';
import type { SeriesPresentation } from './presentation/seriesConfig';
import type { ValueFormat } from './presentation/valueFormat';

export type RenderAdapterErrorCode = 'missing_columns' | 'malformed_rows';

export class RenderAdapterError extends Error {
  readonly name = 'RenderAdapterError';

  constructor(
    readonly code: RenderAdapterErrorCode,
    readonly missingColumns?: readonly string[],
    message?: string,
  ) {
    super(
      message ??
        (code === 'missing_columns' && missingColumns?.length
          ? `Required column(s) missing from rows: ${missingColumns.join(', ')}`
          : code === 'malformed_rows'
            ? 'Rows input is malformed'
            : 'Render adapter error'),
    );
    Object.setPrototypeOf(this, RenderAdapterError.prototype);
  }
}

export interface RenderAdapterResult<TProps> {
  readonly props: TProps;
  readonly error?: RenderAdapterError;
  readonly malformed?: boolean;
}

/** Anything that survives `JSON.parse(JSON.stringify(...))` unchanged. */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/**
 * Authored props forwarded to the renderer as-is.
 *
 * Typed to JSON on purpose. An adapter emits props, and props that a host has
 * to finish assembling are not props — so the values it forwards have to be
 * data all the way down. An open `Record<string, unknown>` is how a formatter
 * function ends up inside a shape everything downstream believes is
 * serializable.
 */
export type ChartPropPassthrough = Readonly<Record<string, JsonValue>>;

/**
 * How a chart should look, as distinct from what it should draw.
 *
 * Adapters need this because the props a chart expects are not all derivable
 * from rows: labels, colors, and formats are authored, and an adapter that
 * cannot see them can only emit a fragment for someone else to complete.
 */
export interface ChartPresentation {
  /** Authored series, in author order. Labels and colors come from here. */
  readonly series?: readonly SeriesPresentation[];
  readonly height?: number;
  readonly fillContainer?: boolean;
  readonly passthrough?: ChartPropPassthrough;
}

/**
 * Adapter output: the props a chart component takes.
 *
 * The index signature carries the forwarded authored props. Core fields stay
 * precisely typed; everything else is whatever the document authored, which is
 * validated by the host before it reaches the adapter.
 */
export type AdapterProps<TCoreProps> = TCoreProps & {
  readonly [prop: string]: unknown;
};

/** Line and bar mapping: X axis, series keys, optional pivot. */
export interface LineOrBarAdapterConfig {
  readonly pivotBy?: PivotConfig;
  readonly xAxisKey: string;
  readonly series: readonly string[];
  readonly presentation?: LineOrBarPresentation;
}

export interface LineOrBarPresentation extends ChartPresentation {
  readonly yAxisFormat?: ValueFormat;
}

export interface LineOrBarCoreProps {
  readonly data: ChartDataPoint[];
  readonly config: ChartConfig;
  readonly series: string[];
  readonly xAxisKey: string;
  readonly height?: number;
  readonly fillContainer?: boolean;
  readonly yAxisFormat?: NumberFormatSpec;
  readonly seriesColorConfigs?: Record<string, SeriesColorConfig>;
}

export type LineOrBarAdapterProps = AdapterProps<LineOrBarCoreProps>;

export interface WaterfallAdapterConfig {
  readonly segmentKey: string;
  readonly valueKey: string;
  readonly positiveSeriesKey: string;
  readonly negativeSeriesKey: string;
  readonly presentation?: WaterfallPresentation;
}

export interface WaterfallPresentation extends ChartPresentation {
  /** Formats the value axis, which a waterfall draws along X. */
  readonly valueAxisFormat?: ValueFormat;
}

export interface WaterfallCoreProps {
  readonly data: ChartDataPoint[];
  readonly config: ChartConfig;
  /** Positive then negative — the renderer maps palette slots by index. */
  readonly series: [string, string];
  readonly xAxisKey: string;
  readonly height?: number;
  readonly fillContainer?: boolean;
  readonly xAxisFormat?: NumberFormatSpec;
  readonly seriesColorConfigs?: Record<string, SeriesColorConfig>;
}

export type WaterfallAdapterProps = AdapterProps<WaterfallCoreProps>;

export interface DataTableFormatByDimension {
  readonly dimensionKey: string;
  readonly map?: Readonly<Record<string, ValueFormat>>;
}

export interface DataTableColumnDef {
  readonly header: string;
  readonly accessorKey: string;
  readonly format?: ValueFormat;
  readonly align?: 'left' | 'center' | 'right';
  readonly sortable?: boolean;
  readonly filterable?: boolean;
  readonly searchable?: boolean;
  readonly formatByDimension?: DataTableFormatByDimension;
  /**
   * Authored fields the adapter does not interpret, forwarded verbatim.
   *
   * The adapter reads `accessorKey` and `formatByDimension.dimensionKey`
   * because those decide which columns the rows must contain.
   */
  readonly [authored: string]:
    | JsonValue
    | ValueFormat
    | DataTableFormatByDimension
    | undefined;
}

export interface DataTableAdapterConfig {
  readonly columns: readonly DataTableColumnDef[];
  readonly presentation?: DataTablePresentation;
}

export interface DataTablePresentation extends ChartPresentation {
  readonly mode?: 'paginated' | 'scrollable';
  readonly pageSize?: number;
  readonly searchColumns?: readonly string[];
  readonly searchLabel?: string;
}

export interface DataTableCoreProps {
  readonly data: TransformRow[];
  /**
   * Declarative column definitions.
   *
   * This is the one prop an adapter cannot finish: the table component takes
   * resolved column definitions whose cell renderers are functions, and those
   * renderers depend on render-time context the adapter never sees. The host
   * builds them from these definitions at the render call.
   */
  readonly columns: DataTableColumnDef[];
  readonly mode: 'paginated' | 'scrollable';
  readonly pageSize: number;
  readonly searchColumns?: string[];
  readonly searchLabel?: string;
}

export type DataTableAdapterProps = AdapterProps<DataTableCoreProps>;

export interface ComboAdapterConfig
  extends Omit<LineOrBarAdapterConfig, 'presentation'> {
  readonly barSeries: readonly string[];
  readonly lineSeries: readonly string[];
  readonly presentation?: ComboPresentation;
}

export interface ComboPresentation extends ChartPresentation {
  readonly leftYAxisFormat?: ValueFormat;
  readonly rightYAxisFormat?: ValueFormat;
}

export interface ComboCoreProps {
  readonly data: ChartDataPoint[];
  readonly config: ChartConfig;
  readonly barSeries: string[];
  readonly lineSeries: string[];
  readonly xAxisKey: string;
  readonly height?: number;
  readonly fillContainer?: boolean;
  readonly leftYAxisFormat?: NumberFormatSpec;
  readonly rightYAxisFormat?: NumberFormatSpec;
  readonly seriesColorConfigs?: Record<string, SeriesColorConfig>;
}

export type ComboAdapterProps = AdapterProps<ComboCoreProps>;

export interface HeatmapAdapterConfig {
  readonly geoIdKey: string;
  readonly valueKey: string;
  readonly presentation?: HeatmapPresentation;
}

/**
 * How a map should look, named field by field.
 *
 * Every other chart forwards its authored look through `passthrough`, which is
 * bounded to JSON but not to any one chart's prop names. The map's set is small
 * and closed, and it had already been enumerated once — in a host module, which
 * is how a renderer prop contract came to live outside the capability that
 * emits it. Naming the fields here also puts the two map props that take
 * functions out of reach by construction rather than by a runtime strip.
 *
 * Typed against the renderer's own props so a widened union there cannot be
 * silently narrowed here.
 */
export interface HeatmapLook {
  readonly normalization?: HeatmapChartProps['normalization'];
  readonly colorScale?: HeatmapChartProps['colorScale'];
  readonly colorScaleConfig?: HeatmapChartProps['colorScaleConfig'];
  readonly legendVisible?: HeatmapChartProps['legendVisible'];
  readonly legendPosition?: HeatmapChartProps['legendPosition'];
  readonly strokeWidth?: HeatmapChartProps['strokeWidth'];
  readonly scaleFactor?: HeatmapChartProps['scaleFactor'];
  readonly zoomable?: HeatmapChartProps['zoomable'];
  /**
   * Authored map framing. Serializable, so it belongs in the closed look
   * contract; the function-valued tooltip props stay excluded.
   */
  readonly projectionConfig?: HeatmapChartProps['projectionConfig'];
}

/**
 * `series` and `passthrough` are both dropped: a map has no series, and its
 * look is enumerated above. Leaving `passthrough` would give the same prop two
 * routes to the renderer, which is the drift the enumeration exists to end.
 */
export interface HeatmapPresentation
  extends Omit<ChartPresentation, 'series' | 'passthrough'>,
    HeatmapLook {
  /** Map granularity. Authored strings are narrowed by the host. */
  readonly level?: HeatmapLevel;
  /** Two-digit FIPS state code a county map is restricted to. */
  readonly stateFips?: string;
}

export type HeatmapLevel = HeatmapChartProps['level'];

export type HeatmapDataPoint = HeatmapChartDatum;

export interface HeatmapCoreProps extends HeatmapLook {
  readonly data: HeatmapDataPoint[];
  readonly level: HeatmapLevel;
  readonly stateFips?: string;
  readonly height?: number;
  readonly fillContainer?: boolean;
}

export type HeatmapAdapterProps = AdapterProps<HeatmapCoreProps>;

export interface ScatterAdapterConfig {
  readonly xAxisKey: string;
  readonly yAxisKey: string;
  readonly presentation?: ScatterPresentation;
}

export interface ScatterPresentation extends ChartPresentation {
  /**
   * Legend and tooltip label for the single implicit series.
   *
   * A scatter chart has one series unless the renderer splits it by a grouping
   * column, and when it does the group values supply their own labels. So this
   * is the label for the ungrouped case rather than an entry in `series`.
   */
  readonly seriesLabel?: string;
  readonly xAxisLabel?: string;
  readonly yAxisLabel?: string;
  readonly xAxisDomain?: readonly [number, number];
  readonly yAxisDomain?: readonly [number, number];
  readonly xAxisFormat?: ValueFormat;
  readonly yAxisFormat?: ValueFormat;
}

export interface ScatterCoreProps {
  readonly data: ChartDataPoint[];
  readonly config: ChartConfig;
  readonly xAxisKey: string;
  readonly yAxisKey: string;
  readonly xAxisLabel?: string;
  readonly yAxisLabel?: string;
  readonly xAxisDomain?: [number, number];
  readonly yAxisDomain?: [number, number];
  readonly xAxisFormat?: NumberFormatSpec;
  readonly yAxisFormat?: NumberFormatSpec;
  readonly height?: number;
  readonly fillContainer?: boolean;
}

export type ScatterAdapterProps = AdapterProps<ScatterCoreProps>;

/**
 * Which row a single-figure tile reads, and how to read it.
 *
 * `valuePath` is dotted rather than a plain key because a KPI reads one field
 * out of a row that may nest, and `rowSelector` exists because long-form
 * responses carry every dimension value in separate rows — the tile has to say
 * which one is its own.
 */
export interface KpiAdapterConfig {
  readonly valuePath?: string;
  readonly format?: ValueFormat;
  /** Strict-equality match on dimension columns. Omit for single-row results. */
  readonly rowSelector?: Readonly<Record<string, string | number | boolean>>;
  /** Comparison figure, rendered as the delta beside the value. */
  readonly contextPath?: string;
  readonly contextFormat?: ValueFormat;
  /** Sparkline series, when the tile draws one. */
  readonly trendXPath?: string;
  readonly trendValuePath?: string;
  readonly presentation?: KpiPresentation;
}

/**
 * Everything about a tile that is authored rather than read from rows.
 *
 * `label` arrives resolved: titles carry term templates whose vocabulary
 * belongs to the host, so substituting them here would pull a reporting concept
 * into the capability. Same for `value` — a tile whose figure is a static
 * authored number has no row to read, so the host passes the text and the
 * adapter does not invent data for it.
 */
export interface KpiPresentation {
  readonly label?: string;
  readonly directionality?: 'higher-is-better' | 'lower-is-better';
  readonly statStyle?: KpiStatStyle;
  readonly deltaSubLabel?: string;
  readonly colorValue?: boolean | 'positive' | 'negative' | 'auto-by-value';
  /** Static authored figure, used in place of anything read from rows. */
  readonly staticValue?: string;
  readonly passthrough?: ChartPropPassthrough;
}

export type KpiStatStyle = KPITileProps['statStyle'];

export type KpiCoreProps = Pick<
  KPITileProps,
  | 'label'
  | 'value'
  | 'delta'
  | 'directionality'
  | 'deltaLabel'
  | 'deltaSubLabel'
  | 'colorValue'
  | 'statStyle'
  | 'trendData'
>;

export type KpiAdapterProps = AdapterProps<KpiCoreProps>;

/**
 * A banner of tiles, each reading from its own rows.
 *
 * Rows arrive per tile rather than as one set, because each tile is fetched
 * separately — they can target different metrics, dimensions, and terms, and
 * one row set could not serve them all.
 */
export interface KpiBannerAdapterConfig {
  readonly tiles: readonly KpiBannerTileConfig[];
  readonly presentation?: KpiBannerPresentation;
}

export interface KpiBannerTileConfig extends KpiAdapterConfig {
  /** Identifies which rows in the adapter input belong to this tile. */
  readonly id: string;
}

export interface KpiBannerPresentation {
  readonly passthrough?: ChartPropPassthrough;
}

export interface KpiBannerCoreProps {
  readonly tiles: KpiCoreProps[];
}

export type KpiBannerAdapterProps = AdapterProps<KpiBannerCoreProps>;

/** Rows per tile id, since each banner tile is fetched on its own. */
export type KpiBannerRows = Readonly<Record<string, readonly TransformRow[]>>;

export type RenderAdapterChartType =
  | 'BarChart'
  | 'LineChart'
  | 'DataTable'
  | 'WaterfallChart'
  | 'ComboChart'
  | 'HeatmapChart'
  | 'ScatterChart';

/**
 * Surfaces that read rows and render a figure rather than a plot.
 *
 * Kept separate from {@link RenderAdapterChartType} because a KPI tile is not a
 * chart, and a union that says otherwise propagates the claim into every
 * signature that accepts it — including, eventually, the saved document.
 */
export type RenderAdapterTileType = 'KPI' | 'KPIBanner';

/** A message surface. Not a chart and not a tile. */
export type RenderAdapterCalloutType = 'Callout';

/** Every surface the registry can render. */
export type RenderAdapterComponentType =
  | RenderAdapterChartType
  | RenderAdapterTileType
  | RenderAdapterCalloutType;

/**
 * Authored chrome for a callout fed by a settled source.
 *
 * `heading` becomes the callout label. `emptyCopy` is placed on `footer`
 * because an empty callout renders no body. `errorCopy` becomes
 * `errorContent`. `itemLimit` slices a list before it is returned.
 */
export interface InferenceCalloutConfig {
  readonly heading?: string;
  readonly emptyCopy?: string;
  readonly errorCopy?: string;
  readonly itemLimit?: number;
}
